import axiosInstance from "./AxiosInstance";
import { cachedRequest, clearCache } from "./requestCache";

const API_URL = "/studentfeedbacks";

/* Submitting or editing feedback changes what /pending answers and what
   every summary counts, so the caches those reads sit behind are dropped
   here rather than at each call site -- a widget that still lists a
   session the student just rated is the bug this prevents. */
const invalidateFeedback = () => clearCache("feedback:");

/** The sessions this student attended and has not rated yet. */
export const getPendingFeedback = async (limit = 20) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/pending`, {
      params: { limit },
    });
    const raw = response.data?.data;
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    console.error("Error fetching pending feedback", error);
    // An empty list, not a throw: the widget is an aside on someone
    // else's page and must never be able to take that page down.
    return [];
  }
};

/**
 * File feedback for one session.
 *
 * `topicRatings` is the substance -- [{ topic, rating, comment }] -- and
 * the session `rating` may be left out, in which case the API derives it
 * from the mean of those. `trainerRating` rates the person rather than
 * the class.
 */
export const createStudentFeedback = async (payload) => {
  try {
    const response = await axiosInstance.post(API_URL, payload);
    invalidateFeedback();
    return response.data;
  } catch (error) {
    console.error("Error submitting feedback", error.response?.data || error);
    throw error;
  }
};

export const updateStudentFeedback = async (id, payload) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${id}`, payload);
    invalidateFeedback();
    return response.data;
  } catch (error) {
    console.error("Error updating feedback", error.response?.data || error);
    throw error;
  }
};

/**
 * Role-scoped by the server: a student sees their own rows, a trainer
 * sees their own sessions with the student identity stripped, an admin
 * sees everything. There is no `role` parameter on purpose -- the caller
 * does not get to say who it is.
 */
export const listStudentFeedback = async (params = {}) => {
  try {
    const response = await axiosInstance.get(API_URL, { params });
    return {
      data: Array.isArray(response.data?.data) ? response.data.data : [],
      pagination: response.data?.pagination || null,
    };
  } catch (error) {
    console.error("Error fetching feedback", error);
    throw error;
  }
};

export const getFeedbackSummary = async (params = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/summary`, { params });
    return response.data?.data || null;
  } catch (error) {
    console.error("Error fetching feedback summary", error);
    return null;
  }
};

/* The review sections on /session-feedback. `kind` is "bad" (rated 1-3) or
   "good" (4-5); the rest are the page's filters, and the server scopes the
   answer exactly as it scopes the list. `range` is { min, max } -- the
   ratings the API counted as that kind, for the wording. */
const REVIEW_RANGE = { bad: { min: 1, max: 3 }, good: { min: 4, max: 5 } };

/**
 * Batch + trainer pairs with reviews of `kind`, the most first.
 * Items: { batchId, batchName, trainerId, trainerName, responses,
 * reviewCount, reviewShare, averageReviewRating }. Returns
 * { items, totalPairs, kind, range }.
 */
export const getReviewPairs = async (params = {}) => {
  const kind = params.kind === "good" ? "good" : "bad";
  const empty = { items: [], totalPairs: 0, kind, range: REVIEW_RANGE[kind] };
  try {
    const response = await axiosInstance.get(`${API_URL}/review-pairs`, { params: { ...params, kind } });
    return response.data?.data || empty;
  } catch (error) {
    console.error("Error fetching review pairs", error);
    // An empty section, never a broken page.
    return empty;
  }
};

/**
 * The reviews of `kind` by trainer or by batch (`groupBy`), a page at a
 * time. Each item carries reviewCount, studentCount, averageReviewRating
 * and `rank` (its place in the whole list, 1 = the most). `counts` is the
 * size of both lists, for the tab badges.
 */
export const getReviewLists = async (params = {}) => {
  const kind = params.kind === "good" ? "good" : "bad";
  try {
    const response = await axiosInstance.get(`${API_URL}/review-lists`, { params: { ...params, kind } });
    const meta = response.data?.meta || {};
    return {
      data: Array.isArray(response.data?.data) ? response.data.data : [],
      pagination: response.data?.pagination || null,
      counts: meta.counts || { trainers: 0, batches: 0 },
      range: meta.range || REVIEW_RANGE[kind],
    };
  } catch (error) {
    console.error("Error fetching review lists", error);
    throw error;
  }
};

/**
 * Per-trainer aggregates for the dashboard's comparison box.
 *
 * Administrators only — the API returns an empty list to anyone else,
 * so there is no role check here to get wrong.
 */
export const getTrainerFeedbackStats = async (params = {}) => {
  try {
    // Shared for a minute: the dashboard box mounts twice under StrictMode and
    // again on every visit, and the aggregate reads every feedback row. A
    // feedback write clears the "feedback:" prefix, so a new rating shows.
    const response = await cachedRequest(
      `feedback:trainer-stats:${JSON.stringify(params)}`,
      60000,
      () => axiosInstance.get(`${API_URL}/trainer-stats`, { params })
    );
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error) {
    console.error("Error fetching trainer feedback stats", error);
    // A box that cannot load is an empty box, never a broken dashboard.
    return [];
  }
};

/**
 * Feedback submitted after `since`, for the staff overlay. Scoped by the
 * server exactly like the list: an admin gets every row with the student
 * named, a trainer only their own sessions with no student identity.
 *
 * Returns { items, total, serverTime } -- pass serverTime back as the next
 * `since`. Without `since` it returns no items and only sets the start.
 * Silent: a background check must never raise the "Working" overlay.
 */
export const getRecentFeedback = async ({ since, limit = 10 } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/recent`, {
      params: { ...(since ? { since } : {}), limit },
      silent: true,
    });
    return response.data?.data || null;
  } catch (error) {
    console.error("Error fetching recent feedback", error);
    // Null, not a throw: the overlay is an aside and must never break a page.
    return null;
  }
};

/** Batches and trainers that actually appear in the caller's feedback. */
export const getFeedbackFilters = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/filters`);
    return response.data?.data || { batches: [], trainers: [] };
  } catch (error) {
    console.error("Error fetching feedback filters", error);
    return { batches: [], trainers: [] };
  }
};

/**
 * The per-topic block is stored as a JSON string, so every reader would
 * otherwise parse it -- and one of them would forget the try/catch on a
 * row written before the field existed.
 */
export const readTopicRatings = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};
